import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import DramaCard from '@/components/feature/DramaCard';
import { getDramaById, dramas } from '@/mocks/dramas';

export default function DramaDetail() {
  const { id } = useParams<{ id: string }>();
  const drama = useMemo(() => getDramaById(id || ''), [id]);
  const relatedDramas = useMemo(() => {
    if (!drama) return [];
    return dramas.filter((d) => d.category === drama.category && d.id !== drama.id).slice(0, 6);
  }, [drama]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [id]);

  if (!drama) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-background-100 border border-background-200/50 flex items-center justify-center mb-4">
              <i className="ri-error-warning-line text-foreground-400 text-3xl"></i>
            </div>
            <p className="text-foreground-500 text-base">未找到该剧集</p>
            <Link
              to="/"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20">
        {/* Breadcrumb */}
        <nav className="max-w-7xl mx-auto px-4 md:px-6 py-4" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">首页</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li><Link to={`/category/${encodeURIComponent(drama.category)}`} className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">{drama.category}</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li className="text-foreground-800 font-medium truncate">{drama.title}</li>
          </ol>
        </nav>

        {/* Drama Detail */}
        <article className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">
          <div className="flex flex-col md:flex-row gap-6 md:gap-10">
            {/* Poster */}
            <div className="flex-shrink-0 mx-auto md:mx-0 w-48 md:w-64">
              <div className="aspect-[2/3] rounded-lg overflow-hidden">
                <img
                  src={drama.posterSrc}
                  alt={`${drama.title} - 免费在线观看高清`}
                  title={`${drama.title} 高清在线观看`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                {drama.title}
              </h1>
              {drama.englishTitle && (
                <p className="text-foreground-500 text-sm mb-4">{drama.englishTitle}</p>
              )}

              <div className="flex flex-wrap items-center gap-3 mb-5">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-sm font-bold whitespace-nowrap">
                  <i className="ri-star-fill text-xs"></i> {drama.rating}
                </span>
                <span className="text-foreground-600 text-sm">{drama.category}</span>
                <span className="text-foreground-600 text-sm">{drama.year}</span>
                <span className="text-foreground-600 text-sm">{drama.region}</span>
                <span className="text-foreground-600 text-sm">{drama.episodeCount}集</span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                  drama.status === '更新中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {drama.status}
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-5">
                {drama.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-accent-100/80 text-accent-700 text-xs font-medium whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-foreground-600 text-sm leading-relaxed mb-6">
                {drama.description}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-6">
                <div className="flex items-center gap-2">
                  <span className="text-foreground-400 whitespace-nowrap">导演：</span>
                  <span className="text-foreground-700">{drama.director}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-foreground-400 whitespace-nowrap">主演：</span>
                  <span className="text-foreground-700">{drama.actors?.join('、')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-foreground-400 whitespace-nowrap">状态：</span>
                  <span className="text-foreground-700">{drama.updateInfo}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-play-circle-fill text-lg"></i> 立即播放
                </button>
                <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-100 border border-background-200/50 text-foreground-700 font-medium text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line"></i> 收藏
                </button>
                <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-100 border border-background-200/50 text-foreground-700 font-medium text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line"></i> 分享
                </button>
              </div>
            </div>
          </div>

          {/* Episode List - placeholder */}
          <section className="mt-10 pt-8 border-t border-background-200/50" aria-label="分集列表">
            <h2 className="text-lg font-bold text-foreground-900 mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
              分集列表
            </h2>
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3">
              {Array.from({ length: Math.min(drama.episodeCount, 20) }, (_, i) => (
                <button
                  key={i + 1}
                  className="w-full py-2.5 rounded-lg bg-background-100 border border-background-200/50 text-foreground-600 text-sm hover:border-primary-500/40 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  第{i + 1}集
                </button>
              ))}
              {drama.episodeCount > 20 && (
                <button className="w-full py-2.5 rounded-lg bg-background-100 border border-background-200/50 text-foreground-500 text-sm hover:border-primary-500/40 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                  更多...
                </button>
              )}
            </div>
          </section>
        </article>

        {/* Related Dramas */}
        {relatedDramas.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100/50" aria-label="相关推荐">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-3 mb-6">
                <i className="ri-thumb-up-line text-primary-500 text-xl"></i>
                <h2 className="text-xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
                  猜你喜欢
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedDramas.map((item) => (
                  <DramaCard key={item.id} drama={item} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Schema.org */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": `${drama.title} - 免费在线观看高清全集`,
            "description": drama.description,
            "image": drama.posterSrc,
            "datePublished": `${drama.year}-01-01`,
            "author": { "@type": "Person", "name": drama.director },
            "publisher": { "@type": "Organization", "name": "被窝影院" },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://www.beiwoyingyuan.com/drama/${drama.id}`
            }
          })
        }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.beiwoyingyuan.com/" },
              { "@type": "ListItem", "position": 2, "name": drama.category, "item": `https://www.beiwoyingyuan.com/category/${encodeURIComponent(drama.category)}` },
              { "@type": "ListItem", "position": 3, "name": drama.title, "item": `https://www.beiwoyingyuan.com/drama/${drama.id}` }
            ]
          })
        }} />
      </main>

      <Footer />
    </div>
  );
}